/**
 * HoverCard
 *
 * Mostra um card flutuante ao passar o mouse sobre um elemento (trigger).
 * - Anexa os handlers/ref DIRETO no elemento filho via cloneElement — funciona
 *   tanto para itens em fluxo (lista da Agenda) quanto para blocos posicionados
 *   de forma absoluta (grids de semana/dia), onde um wrapper teria altura 0.
 * - Apenas desktop (gate `(hover: hover)`) — em touch não abre; o clique segue normal.
 * - Portal para document.body, posição fixa, com consciência de borda.
 * - `pointer-events: none` no card: é só preview, não rouba o hover (sem flicker).
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface HoverCardProps {
  card: React.ReactNode;
  children: React.ReactElement;
  delay?: number;
  cardWidth?: number;
  cardHeight?: number;
}

export const HoverCard: React.FC<HoverCardProps> = ({
  card,
  children,
  delay = 150,
  cardWidth = 326,
  cardHeight = 420,
}) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const canHover = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;

  const compute = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: preferir à direita; se não couber, à esquerda; senão clampar.
    let left = r.right + margin;
    if (left + cardWidth > vw - 8) left = r.left - margin - cardWidth;
    if (left < 8) left = Math.min(Math.max(8, r.left), Math.max(8, vw - cardWidth - 8));

    // Vertical: alinhar ao topo do item, clampando na viewport.
    let top = r.top;
    if (top + cardHeight > vh - 8) top = Math.max(8, vh - cardHeight - 8);
    if (top < 8) top = 8;

    setPos({ top, left });
  }, [cardWidth, cardHeight]);

  const handleEnter = useCallback(() => {
    if (!canHover) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      compute();
      setOpen(true);
    }, delay);
  }, [canHover, compute, delay]);

  const handleLeave = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setOpen(false);
  }, []);

  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current); }, []);

  const child = React.Children.only(children) as React.ReactElement<any>;
  const childProps = child.props as {
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const childRef = (child as any).ref as React.Ref<HTMLElement> | undefined;

  const setRefs = (node: HTMLElement | null): void => {
    triggerRef.current = node;
    if (typeof childRef === 'function') childRef(node);
    else if (childRef && typeof childRef === 'object') {
      (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  };

  const cloned = React.cloneElement(child, {
    ref: setRefs,
    onMouseEnter: (e: React.MouseEvent) => { childProps.onMouseEnter?.(e); handleEnter(); },
    onMouseLeave: (e: React.MouseEvent) => { childProps.onMouseLeave?.(e); handleLeave(); },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);

  return (
    <>
      {cloned}
      {open && pos && createPortal(
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 60, pointerEvents: 'none' }}>
          {card}
        </div>,
        document.body,
      )}
    </>
  );
};

HoverCard.displayName = 'HoverCard';
